import { useState, useEffect } from 'react';
import styled from 'styled-components';

import speaker from 'src/assets/images/user-mobile/parking/notice-admin.svg';
import addNotice from 'src/assets/images/user-mobile/parking/add-notice.svg';
import { apiAnnouncement } from 'src/apis/utils/announcement';
import ConfirmModal from '../../ConfirmModal';

interface ParkingAdminNoticeProps {
  announcement: string;
  announcementEnabled: boolean;
  refetch: () => Promise<void>;
  festivalId: number;
}

export default function ParkingAdminNotice({
  announcement,
  announcementEnabled,
  refetch,
  festivalId,
}: ParkingAdminNoticeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [notice, setNotice] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    description: '',
    confirmText: '',
    confirmColor: 'black' as 'blue' | 'red' | 'black',
    onConfirm: () => {},
  });

  let parkingId = 0;
  if (festivalId === 7) {
    parkingId = 1;
  } else if (festivalId === 9) {
    parkingId = 2;
  }
  useEffect(() => {
    if (announcementEnabled) {
      setNotice(announcement);
    }
  }, [announcement, announcementEnabled]);

  const handlePostNotice = async () => {
    try {
      await apiAnnouncement.postAnnouncement(parkingId, {
        announcement: notice,
        announcementEnabled: true,
      });
      await refetch(); // 상태 최신화
      setToastMessage('안내 공지가 올라갔습니다.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancelNotice = async () => {
    try {
      await apiAnnouncement.postAnnouncement(parkingId, {
        announcement: '',
        announcementEnabled: false,
      });
      setNotice('');
      await refetch(); // 상태 최신화
      setToastMessage('안내 공지가 삭제됐습니다.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = (type: 'upload' | 'edit' | 'delete') => {
    if (type === 'upload') {
      setModalState({
        isOpen: true,
        title: '안내공지 올리기',
        description: '안내 공지를 등록하시겠습니까?',
        confirmText: '올리기',
        confirmColor: 'black',
        onConfirm: async () => {
          await handlePostNotice();
          closeModal();
        },
      });
    } else if (type === 'edit') {
      setModalState({
        isOpen: true,
        title: '안내공지 수정',
        description: '안내 공지를 수정하시겠습니까?',
        confirmText: '수정하기',
        confirmColor: 'black',
        onConfirm: async () => {
          await handlePostNotice();
          closeModal();
        },
      });
    } else if (type === 'delete') {
      setModalState({
        isOpen: true,
        title: '안내공지 삭제',
        description: '안내 공지를 삭제하시겠습니까?',
        confirmText: '삭제하기',
        confirmColor: 'red',
        onConfirm: async () => {
          await handleCancelNotice();
          closeModal();
        },
      });
    }
  };

  const closeModal = () => {
    setModalState(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <NoticeContainer>
      {showToast && <Toast>{toastMessage}</Toast>}
      <TitleContainer onClick={() => setIsOpen(prev => !prev)}>
        <TitleBox>
          <img src={speaker} alt="speaker" width={20} height={20} />
          <NoticeTitle>주차/교통 공지 추가</NoticeTitle>
        </TitleBox>
        <ToggleButton $isOpen={isOpen}>
          <img src={addNotice} alt="toggle" width={24} height={24} />
        </ToggleButton>
      </TitleContainer>

      <WriteContainer $isOpen={isOpen}>
        <WriteInput
          placeholder="주차장 만차, 교통 상황 등 안내사항을 작성하세요."
          value={notice}
          onChange={e => setNotice(e.target.value)}
          wrap="hard"
        />
        <ButtonGroup>
          <CancelNoticeButton onClick={() => openModal('delete')}>
            공지 내리기
          </CancelNoticeButton>
          <WriteNoticeButton
            onClick={() => openModal(announcementEnabled ? 'edit' : 'upload')}
          >
            {announcementEnabled ? '공지 수정하기' : '공지 올리기'}
          </WriteNoticeButton>
        </ButtonGroup>
      </WriteContainer>

      <ConfirmModal
        isOpen={modalState.isOpen}
        title={modalState.title}
        description={modalState.description}
        confirmText={modalState.confirmText}
        confirmColor={modalState.confirmColor}
        onConfirm={modalState.onConfirm}
        onCancel={closeModal}
      />
    </NoticeContainer>
  );
}

const NoticeContainer = styled.div`
  width: 100%;
  background: #fff;
  padding: 12px 16px;
  margin-bottom: 12px;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 16px;
  background: rgba(48, 48, 48, 0.8);
  backdrop-filter: blur(2px);
  color: #fff;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.35px;

  z-index: 999;
  animation: fadeInOut 1.5s ease;

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
    10% {
      opacity: 0.8;
      transform: translateX(-50%) translateY(0);
    }
    90% {
      opacity: 0.8;
      transform: translateX(-50%) translateY(0);
    }
    100% {
      opacity: 0;
      transform: translateX(-50%) translateY(10px);
    }
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoticeTitle = styled.div`
  color: #111;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: -0.45px;
`;

const ToggleButton = styled.button<{ $isOpen: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    transition: transform 0.3s ease;
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(45deg)' : 'rotate(0deg)')};
  }
`;

const WriteContainer = styled.div<{ $isOpen: boolean }>`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? '300px' : '0')};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: all 0.3s ease;
  margin-top: ${({ $isOpen }) => ($isOpen ? '12px' : '0')};
`;

const WriteInput = styled.textarea`
  width: 100%;
  height: 80px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  resize: none;
  color: #111;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.4px;
  margin-bottom: 12px;
`;

const ButtonGroup = styled.div`
  display: flex;
  margin-top: 8px;
  justify-content: flex-end;
  gap: 8px;
`;

const CancelNoticeButton = styled.button`
  padding: 9px 16px;
  background: #fef6f6;
  color: #df0000;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  font-size: 15px;
`;

const WriteNoticeButton = styled.button`
  padding: 9px 16px;
  background: #f2f2f5;
  color: #111;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  font-size: 15px;
`;
